import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Globe, Brain, Users, Building2, GraduationCap,
  Landmark, MessageCircle, ArrowRight, CheckCircle2,
  Star, BarChart3, Languages, Calendar, ChevronRight,
  Mail,
} from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const heroImage = "https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
const teamImage  = "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const aiImage    = "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const features = [
  { icon: Brain,     title: "AI Assessment",         description: "Automatic CEFR proficiency scoring and real-time coaching during every session." },
  { icon: Users,     title: "Peer Exchange",          description: "50/50 tandem sessions with native speakers worldwide — the most effective learning method." },
  { icon: BarChart3, title: "Progress Analytics",    description: "Real-time dashboards for HR and academic directors to track ROI and engagement." },
  { icon: Languages, title: "8,000+ Languages",      description: "From English and Mandarin to rare and endangered languages — every community covered." },
  { icon: Calendar,  title: "Cultural Events",       description: "Join or create language meetups and immersion workshops in any city, worldwide." },
  { icon: Globe,     title: "Global Scale",          description: "From 10-person teams to national deployments. Lingua2 grows with your organization." },
];

const orgTypes = [
  { icon: Building2,     title: "Enterprises",   href: "/enterprise",   description: "Build multilingual workforces, reduce communication friction, and drive global expansion." },
  { icon: GraduationCap, title: "Universities",  href: "/universities", description: "Enhance student language programs, support international exchange, and document outcomes." },
  { icon: Landmark,      title: "Governments",   href: "/government",   description: "Train civil servants, preserve minority languages, and deliver multilingual public services." },
];

const testimonials = [
  { name: "Sarah Chen",      role: "Head of L&D, TechCorp International",   quote: "Lingua2 transformed how our 3,000+ employees across 40 countries communicate. ROI was measurable within 60 days.",              rating: 5 },
  { name: "Dr. Marco Rossi", role: "Director of International Programs, State University", quote: "Students' language proficiency improved 40% in one semester. The AI-tandem combination is genuinely unique.",   rating: 5 },
  { name: "Ana García",      role: "Chief HR Officer, GlobalLogistics SA",   quote: "Fewer miscommunications, faster onboarding for international transfers, and much stronger team cohesion.",                   rating: 5 },
];

const faqs = [
  { q: "What is a language exchange (tandem)?",          a: "A tandem connects two people who want to learn each other's native language. Sessions are 50/50 — half in one language, half in the other. Both participants practice and teach simultaneously." },
  { q: "How does the AI enhance learning?",              a: "Our AI analyzes conversations in real time, assesses language level, corrects grammar, suggests vocabulary, and tracks individual progress — acting as a silent coach during every session." },
  { q: "Can Lingua2 integrate with our HR systems?",     a: "Yes. We offer API integrations with SAP SuccessFactors, Workday, BambooHR, Moodle, Canvas, and custom systems." },
  { q: "What is the minimum number of users?",           a: "Plans start from 10 users. We scale to enterprise-wide deployments for thousands. Contact us for custom pricing." },
  { q: "How is progress measured?",                     a: "Administrators receive detailed monthly reports on participation, language level progress, session frequency, and engagement metrics. Custom dashboards available." },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-background">

      {/* ─── HERO ─── */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Global business team" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 hero-image-overlay" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-5">
              Language Exchange Platform
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold text-white leading-[1.08] mb-6">
              Break Language
              <br />
              <span className="text-primary">Barriers</span> at Scale
            </h1>
            <p className="text-lg text-white/75 leading-relaxed mb-6 max-w-lg">
  Lingua2 provides enterprises, universities, and government institutions with an AI-powered language exchange platform designed to deliver measurable outcomes at global scale.
</p>
<p className="text-base text-white/70 leading-relaxed mb-10 max-w-lg">
  Through the platform, your members can easily connect with peers within your organization to practice languages, as well as discover additional language partners both on-site and online. They also gain access to a diverse network of professional instructors alongside a community of users interested in free language exchange.
</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" asChild className="bg-primary hover:bg-primary-dark text-primary-foreground font-semibold shadow-primary transition-fast">
                <Link to="/contact">Contact Us <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-white/30 text-white hover:bg-white/10 bg-transparent transition-fast">
                <Link to="/how-it-works">How It Works</Link>
              </Button>
            </div>

            <div className="flex items-center gap-5 mt-10">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />)}
                <span className="ml-1.5 text-sm text-white/70">4.9 / 5</span>
              </div>
              <span className="text-white/30">|</span>
              <span className="text-sm text-white/70">500+ organizations worldwide</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className="border-b border-border py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: "500+",  label: "Organizations" },
              { value: "8,000+", label: "Languages" },
              { value: "2M+",   label: "Sessions completed" },
              { value: "98%",   label: "Renewal rate" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-display font-bold text-foreground mb-1">{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOR ORGANIZATIONS ─── */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Built for Organizations"
            title="One Platform, Every Institution"
            description="Whether you lead a multinational company, a research university, or a public institution — Lingua2 adapts to your scale."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {orgTypes.map((org) => (
              <Link key={org.title} to={org.href} className="group">
                <div className="h-full flex flex-col p-7 rounded-xl border border-border bg-card card-hover group-hover:border-primary/30">
                  <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-5">
                    <org.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-display font-bold text-foreground mb-2">{org.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-5">{org.description}</p>
                  <div className="flex items-center text-sm font-semibold text-primary gap-1 group-hover:gap-2 mt-auto" style={{transition: "gap 0.2s"}}>
                    Learn more <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 transition-fast">
              <Link to="/contact">Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">How It Works</p>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground leading-tight mb-5">
                From Sign-Up to First Session in 48 Hours
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                Lingua2 combines the proven effectiveness of peer-to-peer language exchange with AI precision — the most efficient language learning method at scale.
              </p>
              <div className="space-y-5">
                {[
                  { step: "01", title: "AI Assessment",       desc: "Every learner gets an instant CEFR proficiency assessment." },
                  { step: "02", title: "Smart Matching",      desc: "Our algorithm pairs participants by language, level, and schedule." },
                  { step: "03", title: "Live 50/50 Sessions", desc: "Practice in both languages. AI guides in real time." },
                  { step: "04", title: "Reports for Leaders", desc: "Admins get clear monthly progress and ROI reports." },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="flex gap-4">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-xs font-display font-bold text-primary-foreground">{step}</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-display font-semibold text-foreground mb-0.5">{title}</h4>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex gap-3">
                <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
                  <Link to="/how-it-works">See Full Process <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="transition-fast">
                  <Link to="/contact">Request Demo</Link>
                </Button>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl-token">
              <img src={teamImage} alt="Corporate language session" className="w-full h-[440px] object-cover" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-background/95 backdrop-blur-sm rounded-xl p-4 shadow-lg-token">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <MessageCircle className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-foreground">Live Session Active</div>
                      <div className="text-xs text-muted-foreground">AI analysis running · 24 min remaining</div>
                    </div>
                    <div className="ml-auto">
                      <span className="inline-flex w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Platform Features"
            title="Everything Your Organization Needs"
            description="A complete language learning ecosystem — AI assessment, peer exchange, certified teachers, and analytics in one platform."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => {
              const { icon: Icon, title, description } = f;
              return (
                <div key={title} className="group p-6 rounded-xl border border-border bg-card card-hover hover:border-primary/25">
                  <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/14 transition-fast">
                    <Icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-sm font-display font-semibold text-foreground mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/ai-features">Explore AI Features <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── AI HIGHLIGHT ─── */}
      <section className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl-token">
              <img src={aiImage} alt="AI language analysis" className="w-full h-[380px] object-cover" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">AI Technology</p>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground leading-tight mb-5">
                AI That Understands Language, Not Just Words
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-7">
                Our proprietary AI goes beyond translation — it assesses proficiency, guides pronunciation, fills vocabulary gaps, and delivers actionable reports for your team leaders.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "CEFR-aligned proficiency assessment (A1–C2)",
                  "Real-time pronunciation and grammar coaching",
                  "Personalized vocabulary & spaced repetition",
                  "Automated post-session progress summaries",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground/80">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
                  <Link to="/ai-features">See AI Features <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="transition-fast">
                  <Link to="/contact">Book a Demo</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Trusted by Leaders"
            title="What Organizations Say"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <Card key={t.name} className="p-6 border border-border shadow-sm-token">
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 mt-auto">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-primary">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Start Your Free Trial <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-24 bg-surface">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="FAQ"
            title="Common Questions"
          />
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-lg px-4 bg-card"
              >
                <AccordionTrigger className="text-sm font-semibold text-foreground py-4 hover:no-underline hover:text-primary transition-fast">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 transition-fast">
              <Link to="/contact">Still have questions? Contact us <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-primary p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-56 h-56 bg-primary-foreground/5 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-primary-foreground mb-3">
                Ready to Build a Multilingual Organization?
              </h2>
              <p className="text-base text-primary-foreground/75 mb-8 max-w-lg mx-auto">
                Join 500+ organizations that trust Lingua2 to develop language skills, drive global collaboration, and measure real results.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold transition-fast">
                  <Link to="/contact">Contact Us <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent transition-fast">
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FLOATING CTA BUTTON ─── */}
      
    </div>
  );
}
